import { useIntl } from "react-intl";
import dayjs from "dayjs";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { Pagination } from "@mantine/core";
import { Reservation } from "@/components/pages/account/_helpers/useFetchUserReservations";
import { StrapiImage } from "@/utils/strapi";
import { defaultCategoryImage } from "@/components/pages/reservation/categories/agencyOption/defaultImages";
import { NotificationIcon } from "@/public/icons/NotificationIcon";
import { LocationCard } from "./locationCard/LocationCard";
import styles from "./Locations.module.scss";

export type LocationsProps = {
  reservationList: Reservation[];
  categoriesImages: {
    [name: string]: StrapiImage[] | undefined;
  };
};

export const Locations = ({
  reservationList,
  categoriesImages,
}: LocationsProps) => {
  const intl = useIntl();
  const [isCurrentActive, setCurrentActive] = useState<boolean>(true);
  const [currentNumber, setCurrentNumber] = useState<number>(0);
  const [pastNumber, setPastNumber] = useState<number>(0);
  const [activePage, setPage] = useState<number>(1);
  const [pageNumber, setPageNumber] = useState<number>(0);

  const [activeReservations, setActiveReservations] = useState<Reservation[]>(
    []
  );

  useEffect(() => {
    const curatedReservations: Reservation[] = [];
    let currentNb = 0,
      pastNb = 0;

    reservationList.map((reservation) => {
      if (dayjs(reservation.travel.endDate).isAfter(dayjs())) {
        currentNb += 1;
        if (isCurrentActive) {
          curatedReservations.push(reservation);
        }
      } else {
        pastNb += 1;
        if (!isCurrentActive) {
          curatedReservations.push(reservation);
        }
      }
    });

    setActiveReservations(
      curatedReservations.filter((reservation, index) => {
        if ((activePage - 1) * 4 <= index && index < activePage * 4) {
          return reservation;
        }
      })
    );
    setCurrentNumber(currentNb);
    setPastNumber(pastNb);
    setPageNumber(Math.ceil(curatedReservations.length / 4));
  }, [reservationList, isCurrentActive, activePage]);

  return (
    <div className={styles.content}>
      <div className={styles.notifications}>
        <div className={styles.notificationsContainer}>
          <div className={styles.notificationsTitle}>
            <div className={styles.label}>
              {intl.formatMessage({ id: "notifications" })}
            </div>
            <div className={styles.notificationCount}>
              <NotificationIcon></NotificationIcon>
              <div className={styles.typeCount}>0</div>
            </div>
          </div>
          <div className={styles.notificationsList}>
            <div className={styles.emptyCardContainer}>
              <div className={styles.emptyCardTitleContainer}>
                <div className={styles.emptyCardIcon}>
                  <NotificationIcon color="#fff" size="14"></NotificationIcon>
                </div>
                <div className={styles.emptyCardTitle}>
                  {intl.formatMessage({
                    id: "emptyNotificationTitle",
                  })}
                </div>
              </div>
              <div className={styles.emptyCardMessage}>
                {intl.formatMessage({
                  id: "emptyNotificationMessage",
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.locations}>
        <div className={styles.label}>
          {intl.formatMessage({ id: "locations" })}
        </div>
        <div className={styles.locationTypes}>
          <div
            className={classNames(styles.locationType, {
              [styles.active]: isCurrentActive,
            })}
            onClick={() => setCurrentActive(true)}
          >
            <div className={styles.typeLabel}>
              {intl.formatMessage({ id: "current" })}
            </div>
            <div className={styles.typeCount}>{currentNumber}</div>
          </div>
          <div
            className={classNames(styles.locationType, {
              [styles.active]: !isCurrentActive,
            })}
            onClick={() => setCurrentActive(false)}
          >
            <div className={styles.typeLabel}>
              {intl.formatMessage({ id: "past" })}
            </div>
            <div className={styles.typeCount}>{pastNumber}</div>
          </div>
        </div>
        <div className={styles.locationsList}>
          {activeReservations.length === 0 && (
            <div className={styles.emptyCardContainer}>
              <div className={styles.emptyCardTitleContainer}>
                <div className={styles.emptyCardIcon}>
                  <NotificationIcon color="#fff" size="14"></NotificationIcon>
                </div>
                <div className={styles.emptyCardTitle}>
                  {intl.formatMessage({
                    id: isCurrentActive
                      ? "emptyCurrentReservationTitle"
                      : "emptyPastReservationTitle",
                  })}
                </div>
              </div>
            </div>
          )}
          {activeReservations.map((reservation, index) => {
            const image = categoriesImages[
              reservationList[index].categoryChoice.reference + "-DEFAULT"
            ] ?? [defaultCategoryImage];

            return (
              <LocationCard
                key={reservation.bookingId}
                reservation={reservation}
                categoryImage={image[0]}
              />
            );
          })}
        </div>
        <div className={styles.pagination}>
          {pageNumber > 1 && (
            <Pagination
              page={activePage}
              onChange={setPage}
              total={pageNumber}
            />
          )}
        </div>
      </div>
    </div>
  );
};
